// ─────────────────────────────────────────────────────────────────────────────
// RefundPolicyCard — the Auto / Semi-Auto / Manual refund-mode switcher.
//
// Lives in Platform Settings (not on the Transactions page) because it's a
// global platform policy, not a per-transaction action. Mirrors the layout
// the Razorpay/Stripe admin consoles use for similar "refund policy" cards:
// big radio-card per mode, 2-step commit so a misclick never silently
// re-routes future refund flows.
//
// Exports: <RefundPolicyCard /> (self-contained, fetches + saves its own state).
// ─────────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from 'react';
import {
  MdAutoAwesome, MdGavel, MdTouchApp, MdTune, MdCheck,
} from 'react-icons/md';
import { adminAPI } from '../../services/api';

const TEXT   = 'var(--text-primary)';
const SUBTLE = '#94a3b8';
const BORDER = 'rgba(148,163,184,0.18)';
const CARD   = '#111827';
const ACCENT = '#f59e0b';
const GREEN  = '#10b981';
const BLUE   = '#60a5fa';

export default function RefundPolicyCard({ onSaved }) {
  const [mode, setMode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => { (async () => {
    const { data, error } = await adminAPI.getRefundMode();
    if (error) { setErr(error); return; }
    setMode(data?.mode || 'auto');
  })(); }, []);

  const commit = async () => {
    if (!pending) return;
    if (!window.confirm(
      `Switch refund policy from ${(mode || '').toUpperCase()} to ${pending.toUpperCase()}?\n\n` +
      `Existing refunds will not be re-issued. Every future cancellation will follow the new rule.`
    )) return;
    setSaving(true);
    const { data, error } = await adminAPI.setRefundMode(pending);
    setSaving(false);
    if (error) { alert('Could not switch policy:\n' + error); return; }
    setMode(data?.mode || pending);
    setPending(null);
    if (onSaved) onSaved(data?.mode || pending);
  };

  if (mode == null) {
    return (
      <div style={S}>
        <div style={{color:SUBTLE, fontSize:13}}>Loading refund policy…</div>
        {err && <div style={{color:'#f87171', fontSize:12, marginTop:6}}>{err}</div>}
      </div>
    );
  }

  const isPending = !!pending && pending !== mode;
  const opts = [
    {
      v: 'auto', label: 'Auto', color: GREEN, icon: <MdAutoAwesome/>,
      headline: 'Instant Razorpay refund · no admin needed',
      bullets: [
        'Patient cancels → tiered refund on consult fee only',
        '  ≥5h: 100% · ≥4h: 75% · ≥3h: 50% · ≥2h: 25% · <2h: 0%',
        'Hospital cancels → full refund (incl. platform fee + GST)',
        'Razorpay handles UTR + bank credit automatically',
      ],
    },
    {
      v: 'semi-auto', label: 'Semi-Auto', color: ACCENT, icon: <MdTouchApp/>,
      headline: 'Every refund waits for admin Approve before it pays out',
      bullets: [
        'Patient, Hospital and Admin cancels → sit at "Requested"',
        'Patient sees "refund initiated" instantly in the app',
        'Money only moves when admin clicks Approve in the Refunds tab',
        'Recommended default — catches disputes before payout',
      ],
    },
    {
      v: 'manual', label: 'Manual', color: BLUE, icon: <MdGavel/>,
      headline: 'Every refund needs admin approval',
      bullets: [
        'All cancels create "Requested" rows; nothing pays out until you act',
        'Maximum control — slowest payout',
      ],
    },
  ];

  return (
    <div style={S}>
      <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:14}}>
        <div style={{
          width:36, height:36, borderRadius:10, display:'grid', placeItems:'center',
          background:`${ACCENT}22`, color:ACCENT,
        }}><MdTune size={20}/></div>
        <div style={{flex:1}}>
          <div style={{color:TEXT, fontWeight:800, fontSize:16}}>
            Refund Policy
            {isPending && (
              <span style={{
                marginLeft:8, padding:'2px 8px', borderRadius:999,
                background:`${ACCENT}33`, color:ACCENT,
                fontSize:10, fontWeight:900, letterSpacing:0.5,
              }}>UNSAVED CHANGE</span>
            )}
          </div>
          <div style={{color:SUBTLE, fontSize:12, marginTop:2}}>
            {isPending
              ? <>Preview: <strong style={{color:TEXT}}>{pending.toUpperCase()}</strong>. Click <strong style={{color:ACCENT}}>Save</strong> to apply, or Cancel.</>
              : 'Click a card to preview a new policy. Change only takes effect after you press Save.'}
          </div>
        </div>
        {saving && <span style={{color:ACCENT, fontSize:11, fontWeight:700}}>SAVING…</span>}
      </div>

      <div style={{display:'grid', gap:12, gridTemplateColumns:'repeat(auto-fit, minmax(260px, 1fr))'}}>
        {opts.map(o => {
          const active     = mode === o.v;
          const selected   = (pending || mode) === o.v;
          const previewing = isPending && pending === o.v;
          return (
            <button
              key={o.v}
              type="button"
              onClick={() => {
                if (saving) return;
                if (o.v === mode) { setPending(null); return; }
                setPending(o.v);
              }}
              style={{
                textAlign:'left', padding:14,
                border:`2px ${previewing ? 'dashed' : 'solid'} ${selected ? o.color : BORDER}`,
                background: selected ? `${o.color}1A` : 'transparent',
                borderRadius:12,
                cursor: saving ? 'wait' : 'pointer',
                color:TEXT, fontFamily:'inherit',
                position:'relative',
                boxShadow: selected ? `0 0 0 4px ${o.color}22, 0 8px 24px ${o.color}1c` : 'none',
                transition:'all 0.15s ease',
              }}>
              {(active || previewing) && (
                <div style={{
                  position:'absolute', top:10, right:10,
                  width:24, height:24, borderRadius:'50%',
                  background: previewing ? 'transparent' : o.color,
                  color: previewing ? o.color : '#0b1220',
                  border: previewing ? `2px dashed ${o.color}` : 'none',
                  display:'grid', placeItems:'center',
                  boxShadow: previewing ? 'none' : `0 2px 8px ${o.color}88`,
                }}><MdCheck size={16}/></div>
              )}

              <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:8}}>
                <div style={{color:o.color, fontSize:24}}>{o.icon}</div>
                <div style={{ fontWeight:900, fontSize:16, color: selected ? o.color : TEXT, letterSpacing:0.3}}>
                  {o.label}
                  {active && !previewing && (
                    <span style={{
                      marginLeft:8, padding:'2px 8px', borderRadius:999,
                      background:o.color, color:'#0b1220',
                      fontSize:10, fontWeight:900, letterSpacing:0.5,
                    }}>ACTIVE</span>
                  )}
                  {previewing && (
                    <span style={{
                      marginLeft:8, padding:'2px 8px', borderRadius:999,
                      background:'transparent', color:o.color, border:`1px dashed ${o.color}`,
                      fontSize:10, fontWeight:900, letterSpacing:0.5,
                    }}>PREVIEW</span>
                  )}
                </div>
              </div>

              <div style={{color: active ? TEXT : SUBTLE, fontSize:12.5, fontWeight:600, marginBottom:10}}>
                {o.headline}
              </div>
              <ul style={{margin:0, paddingLeft:18, color:SUBTLE, fontSize:12, lineHeight:1.6}}>
                {o.bullets.map((b, i) => <li key={i}>{b}</li>)}
              </ul>
            </button>
          );
        })}
      </div>

      {isPending && (
        <div style={{
          marginTop:12, display:'flex', justifyContent:'flex-end', alignItems:'center',
          gap:10, paddingTop:10, borderTop:`1px dashed ${BORDER}`,
        }}>
          <div style={{flex:1, color:SUBTLE, fontSize:12}}>
            Switching to <strong style={{color:TEXT}}>{pending.toUpperCase()}</strong> affects ALL future cancellations.
          </div>
          <button onClick={() => setPending(null)} disabled={saving} style={{
            padding:'8px 14px', borderRadius:10, fontWeight:700, fontSize:13,
            background:'transparent', border:`1px solid ${BORDER}`, color:TEXT,
            cursor: saving ? 'wait' : 'pointer', fontFamily:'inherit',
          }}>Cancel</button>
          <button onClick={commit} disabled={saving} style={{
            padding:'8px 16px', borderRadius:10, fontWeight:800, fontSize:13,
            background:ACCENT, color:'#0b1220', border:'none',
            cursor: saving ? 'wait' : 'pointer', fontFamily:'inherit',
            boxShadow:`0 4px 14px ${ACCENT}55`,
          }}>{saving ? 'Saving…' : `Save → ${pending.toUpperCase()}`}</button>
        </div>
      )}
    </div>
  );
}

const S = {
  background:CARD, border:`1px solid ${BORDER}`, borderRadius:12,
  padding:16, marginBottom:16,
};
